// services/blockchain.service.js
import { 
  fetchBucketContent, 
  fetchFileContent, 
  deleteFile, 
  encryptData, 
  decryptData,
  removeTxtExtension,
  ensureTxtExtension,
  clearBucketContentCache,
  getUserCredentials,
  ensureS3SafeFileName
} from './api.service';
import CryptoJS from 'crypto-js';

import { 
  getCurrentInheritance, 
  uploadInheritance
} from './endowment.service';

import { UPLOAD_ENDPOINTS, INHERITANCE_ENDPOINTS } from '../utils/constants';

const BUCKET_NAME = 'blockchain';

// Fetch all blockchain phrases
export const fetchPhrases = async (token, forceClearCache = false) => {
  try {
    return await fetchBucketContent(BUCKET_NAME, token, forceClearCache);
  } catch (error) {
    console.error('Error fetching passwords:', error);
    throw error;
  }
};

// View a specific blockchain phrase
export const viewPhrase = async (phraseName, token) => {
  try {
    const fileContent = await fetchFileContent(BUCKET_NAME, phraseName, token);
    const phraseLines = fileContent.split('\n');
    
    // Decrypt each line
    const decryptedPhrases = phraseLines.map(line => 
      decryptPhrase(line)
    );
    
    return {
      alias: removeTxtExtension(phraseName),
      phrases: decryptedPhrases,
      originalName: phraseName // Keep the original name for updates
    };
  } catch (error) {
    console.error('Error viewing blockchain phrase:', error);
    throw error;
  }
};

// Save blockchain phrase (create or update)
export const savePhrase = async (phraseData, originalName = null, token) => {
  try {
    let credentials;
    if (!token) {
      // Try to get from session storage as fallback
      credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }
    
    // If we're updating, delete the old file first
    if (originalName) {
      await deletePhrase(originalName, token);
    }
    
    // Encrypt each phrase
    const encryptedPhrases = phraseData.phrases.map(phrase => 
      encryptPhrase(phrase)
    );
    
    // Prepare data for API
    const data = {
      alias: phraseData.alias,
      phrases: encryptedPhrases
    };
    
    // Send to API
    const response = await fetch(UPLOAD_ENDPOINTS.UPLOAD_PHRASES, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Clear cache after successful save
    await clearBucketContentCache();
    
    return true;
  } catch (error) {
    console.error('Error saving blockchain phrase:', error);
    throw error;
  }
};

// Delete blockchain phrase
export const deletePhrase = async (phraseName, token) => {
  try {
    // First, update any inheritances that contain this phrase
    try {
      await updateInheritancesAfterBlockchainDeletion(phraseName, token);
    } catch (error) {
      console.error('Error updating inheritances:', error);
      // Continue with deletion even if inheritance update fails
    }
    
    return await deleteFile(BUCKET_NAME, phraseName, token);
  } catch (error) {
    console.error('Error deleting blockchain phrase:', error);
    throw error;
  }
};

/**
 * Add blockchain phrase to the user's current inheritance (like endowment page)
 * @param {string} phraseName - Phrase name/filename
 * @param {string} token - Authentication token
 * @returns {Promise<boolean>} Success status
 */
export const addPhraseToCurrentInheritance = async (phraseName, token) => {
  try {
    if (!token) {
      const credentials = getUserCredentials();
      if (!credentials) {
        throw new Error('No authentication information found');
      }
      token = credentials.token;
    }

    const userDetails = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
    const userEmail = userDetails.Email || '';
    
    if (!userEmail) {
      throw new Error('User email not found in session');
    }


    // Get the current (most recent) inheritance for this user
    const currentInheritance = await getCurrentInheritance(token, userEmail);
    
    if (!currentInheritance) {
      throw new Error('No active endowment found. Please create an endowment first in the Endowment Centre.');
    }

    // Use the phrase name as filename (should already have .txt extension)
    const phraseToAdd = ensureTxtExtension(phraseName);

    // Check if the phrase is already in the inheritance
    if (currentInheritance.selectedItems.blockchain && currentInheritance.selectedItems.blockchain.includes(phraseToAdd)) {
      throw new Error('Secure phrase is already added to this endowment');
    }

    // Add the phrase to the existing blockchain items (same logic as handleAddItems in useEndowment)
    const updatedItems = {
      files: [...(currentInheritance.selectedItems.files || [])],
      passwords: [...(currentInheritance.selectedItems.passwords || [])],
      banking: [...(currentInheritance.selectedItems.banking || [])],
      blockchain: [...(currentInheritance.selectedItems.blockchain || []), phraseToAdd]  // Add the new phrase
    };

    // Use uploadInheritance to update the inheritance with the new phrase
    // This works exactly like handleAddItems in useEndowment.js
    await uploadInheritance({
      alias: currentInheritance.alias,
      account: currentInheritance.account,
      selectedItems: updatedItems,
      duration: currentInheritance.duration,
      Email: userEmail
    }, token);

    return true;
  } catch (error) {
    console.error('❌ Error adding phrase to current inheritance:', error);
    throw error;
  }
};

// Generate unique blockchain phrase alias
export const generateUniquePhraseAlias = async (proposedAlias, token) => {
  try {
    // Make sure we're working with a clean alias (no .txt extension)
    proposedAlias = removeTxtExtension(proposedAlias.trim());
    proposedAlias = ensureS3SafeFileName(proposedAlias);
    
    if (proposedAlias === '') {
      return 'Untitled';
    }
    
    const phrases = await fetchPhrases(token);
    
    // Get all existing phrase aliases without .txt extension for comparison
    const existingAliases = phrases.map(phrase => 
      removeTxtExtension(phrase.name).toLowerCase()
    );
    
    // Check if the base alias already exists
    if (!existingAliases.includes(proposedAlias.toLowerCase())) {
      return proposedAlias;
    }
    
    // Name exists, find a unique variation
    let counter = 1;
    let uniqueAlias = `${proposedAlias}(${counter})`;
    
    // Keep incrementing counter until we find a unique name
    while (existingAliases.includes(uniqueAlias.toLowerCase())) {
      counter++;
      uniqueAlias = `${proposedAlias}(${counter})`;
    }
    
    return uniqueAlias;
  } catch (error) {
    console.error('Error generating unique alias:', error);
    throw error;
  }
};

// Helper function to encrypt a phrase
const encryptPhrase = (phrase) => {
  return CryptoJS.AES.encrypt(
    phrase,
    'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
  ).toString();
};

// Helper function to decrypt a phrase
const decryptPhrase = (encryptedPhrase) => {
  try {
    const bytes = CryptoJS.AES.decrypt(
      encryptedPhrase,
      'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
    );
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Error decrypting phrase:', error);
    return '';
  }
};

// Validate phrase input
export const validatePhraseInputs = (alias, phrases) => {
  if (!alias || alias.trim() === '') {
    return {
      isValid: false,
      error: {
        title: 'Input Error',
        message: 'Please enter a phrase alias.',
        details: 'An alias is required to identify your phrase.'
      }
    };
  }

  if (!Array.isArray(phrases)) {
    return {
      isValid: false,
      error: {
        title: 'Input Error',
        message: 'Invalid phrases format.',
        details: 'Please ensure all phrases are entered correctly.'
      }
    };
  }

  // Check for valid phrase count (either 12 or 24)
  if (phrases.length !== 12 && phrases.length !== 24) {
    return {
      isValid: false,
      error: {
        title: 'Input Error',
        message: 'Invalid number of secret phrases.',
        details: 'Either 12 or 24 secret phrases are required.'
      }
    };
  }

  // Check that all phrases are filled
  const emptyPhrases = phrases.filter(phrase => phrase.trim() === '');
  if (emptyPhrases.length > 0) {
    return {
      isValid: false,
      error: {
        title: 'Input Error',
        message: `Please fill in all secret phrases. ${emptyPhrases.length} phrase(s) are empty.`,
        details: `All ${phrases.length} secret phrases are required.`
      }
    };
  }

  return { isValid: true };
};

// Update inheritances after blockchain phrase deletion (placeholder implementation)
const updateInheritancesAfterBlockchainDeletion = async (deletedPhraseAlias, token) => {
  try {
    // Get inheritance list
    const inheritanceList = await getInheritanceList(token);
    
    if (!inheritanceList || inheritanceList.length === 0) {
      return true;
    }
    
    // Process each inheritance
    for (const inheritance of inheritanceList) {
      try {
        // Get inheritance content
        const fileContent = await getInheritance(inheritance.name, token);
        
        // Parse content
        const data = {};
        const lines = fileContent.split('\n');
        lines.forEach(line => {
          const [key, value] = line.split(': ').map(item => item.trim());
          if (key && value) {
            data[key] = value;
          }
        });
        
        const encryptedSelectedItems = data['Selected Items'];
        const alias = data['Alias'];
        const duration = data['Duration'];
        const inheritanceAccount = data['Inheritance Account'];
        
        // Decrypt and parse selected items
        const decryptedSelectedItems = CryptoJS.AES.decrypt(
          encryptedSelectedItems,
          'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
        ).toString(CryptoJS.enc.Utf8);
        
        const selectedItems = JSON.parse(decryptedSelectedItems);
        
        // Check if the inheritance contains the deleted phrase
        const blockchainIndex = selectedItems.blockchain.indexOf(deletedPhraseAlias);
        if (blockchainIndex !== -1) {
          // Remove the deleted phrase
          selectedItems.blockchain.splice(blockchainIndex, 1);
          
          // Update the inheritance
          await deleteInheritance(alias + '.txt', token);
          await saveInheritance(
            removeTxtExtension(alias),
            inheritanceAccount,
            selectedItems,
            duration,
            token
          );
        }
      } catch (error) {
        console.error(`Error processing inheritance ${inheritance.name}:`, error);
        continue; // Continue with next inheritance
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error updating inheritances:', error);
    throw error;
  }
};

// Get inheritance list
export const getInheritanceList = async (token) => {
  try {
    return await fetchBucketContent('inheritance', token);
  } catch (error) {
    console.error('Error fetching inheritance list:', error);
    throw error;
  }
};

// Get inheritance content
export const getInheritance = async (inheritanceName, token) => {
  try {
    return await fetchFileContent('inheritance', inheritanceName, token);
  } catch (error) {
    console.error('Error getting inheritance:', error);
    throw error;
  }
};

// Delete inheritance
export const deleteInheritance = async (inheritanceName, token) => {
  try {
    return await deleteFile('inheritance', inheritanceName, token);
  } catch (error) {
    console.error('Error deleting inheritance:', error);
    throw error;
  }
};

// Save inheritance
export const saveInheritance = async (alias, account, selectedItems, duration, token) => {
  try {
    // Encrypt selected items
    const selectedItemsCiphertext = CryptoJS.AES.encrypt(
      JSON.stringify(selectedItems),
      'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
    ).toString();
    
    // Get user email
    const credentials = getUserCredentials();
    const email = credentials ? credentials.email : '';
    
    // Prepare data for API
    const data = {
      alias: alias,
      account: account,
      selectedItems: selectedItemsCiphertext,
      duration: duration,
      Email: email
    };
    
    // Send to API
    const response = await fetch(INHERITANCE_ENDPOINTS.UPLOAD_ENDPOINTS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return true;
  } catch (error) {
    console.error('Error saving inheritance:', error);
    throw error;
  }
};

// Add phrase to inheritance
export const addPhraseToInheritance = async (phraseName, inheritanceName, token) => {
  try {
    // Get inheritance content
    const fileContent = await getInheritance(inheritanceName, token);
    
    // Parse content
    const data = {};
    const lines = fileContent.split('\n');
    lines.forEach(line => {
      const [key, value] = line.split(': ').map(item => item.trim());
      if (key && value) {
        data[key] = value;
      }
    });
    
    const encryptedSelectedItems = data['Selected Items'];
    const alias = data['Alias'];
    const duration = data['Duration'];
    const inheritanceAccount = data['Inheritance Account'];
    
    // Decrypt and parse selected items
    const decryptedSelectedItems = CryptoJS.AES.decrypt(
      encryptedSelectedItems,
      'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
    ).toString(CryptoJS.enc.Utf8);
    
    const selectedItems = JSON.parse(decryptedSelectedItems);
    
    // Check if the phrase is already in the inheritance
    if (selectedItems.blockchain.includes(phraseName)) {
      throw new Error('Phrase is already included in the inheritance');
    }
    
    // Add the phrase to the inheritance
    selectedItems.blockchain.push(phraseName);
    
    // Update the inheritance
    await deleteInheritance(alias + '.txt', token);
    await saveInheritance(
      removeTxtExtension(alias),
      inheritanceAccount,
      selectedItems,
      duration,
      token
    );
    
    return true;
  } catch (error) {
    console.error('Error adding phrase to inheritance:', error);
    throw error;
  }
};

// Parse inheritance data (helper function)
export const parseInheritanceData = (fileContent) => {
  try {
    const data = {};
    const lines = fileContent.split('\n');
    
    lines.forEach(line => {
      const [key, value] = line.split(': ').map(item => item.trim());
      if (key && value) {
        data[key] = value;
      }
    });
    
    const encryptedSelectedItems = data['Selected Items'];
    const alias = data['Alias'];
    const duration = data['Duration'];
    const inheritanceAccount = data['Inheritance Account'];
    
    // Decrypt selected items
    const decryptedSelectedItems = CryptoJS.AES.decrypt(
      encryptedSelectedItems,
      'k1aY2nI0s4a8Y2nI0s4a8mi0109sdrtw2'
    ).toString(CryptoJS.enc.Utf8);
    
    return {
      alias,
      duration,
      inheritanceAccount,
      selectedItems: JSON.parse(decryptedSelectedItems)
    };
  } catch (error) {
    console.error('Error parsing inheritance data:', error);
    throw error;
  }
};